import React from 'react';
import { Group, Rect, Text, Circle, Line } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { groupByTier, type InteriorDotLite } from '../tierGrouping';
import type { ExplorerFinding } from '../../models/explorer/ExplorerFinding';
import { useCompassViewPrefs } from '../../../../stores/compassViewPrefsStore';

const KIND_ACCENT: Record<string, string> = {
  account: '#2a4a78',
  region: '#466493',
  vpc: '#1ea672',
  subnet: '#8593a8',
  workload: '#2a73e4',
  resource_type: '#5b6b85',
};

const KIND_LABEL: Record<string, string> = {
  resource_type: 'RESOURCE',
};

type DeltaKind = 'added' | 'removed' | 'changed';

const DELTA_TINT: Record<DeltaKind, { stroke: string; fill: string }> = {
  added: { stroke: '#1ea672', fill: '#f1fbf6' },
  removed: { stroke: '#d63a3a', fill: '#fff4f3' },
  changed: { stroke: '#e29400', fill: '#fff9ea' },
};

const SEVERITY_COLOR: Record<string, string> = {
  info: '#2a73e4',
  warn: '#e29400',
  critical: '#d63a3a',
};

const FONT_SANS = 'DM Sans, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const CARD_FILL = '#ffffff';
const CARD_STROKE = '#d6dbe3';
const INK_900 = '#0e1726';
const INK_700 = '#2d3a52';
const INK_500 = '#5b6b85';
const NAVY_100 = '#eef2f8';
const NAVY_900 = '#0a1f3d';
const CARD_RADIUS = 8;
const CARD_FOOTER_H = 40;

function withAlpha(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function dispatchDrillBubble(nodeId: string): void {
  if (!nodeId) return;
  window.dispatchEvent(
    new CustomEvent('compass:toggle-expand', {
      detail: { nodeId, mode: 'drill' },
    }),
  );
}

interface BoundaryChip {
  bubbleId: string;
  targetBubbleId: string;
  targetLabel: string;
  direction: 'in' | 'out';
  count: number;
  kinds: string[];
}

interface CompassContainerCardProps {
  viewModel: { width: number; height: number };
  props: Record<string, unknown>;
}

// Shared card-geometry constants. Must stay in lockstep with
// `bubbleDimensions()` in buildExplorerScene so the card hugs its
// computed bounds without blank padding or overflow.
export const CARD_HEADER_BASE_H = 62;
export const CARD_FINDINGS_ROW_H = 22;
export const CARD_BAND_LABEL_H = 16;
export const CARD_BAND_DOT = 6;
export const CARD_BAND_DOT_GAP = 3;
export const CARD_BAND_GAP = 8;
export const CARD_PADDING_X = 16;
export const CARD_LABEL_W = 120;
export const CARD_MAX_DOTS_PER_BAND = 80;

export function cardHeaderHeight(hasFindings: boolean): number {
  return hasFindings ? CARD_HEADER_BASE_H + CARD_FINDINGS_ROW_H : CARD_HEADER_BASE_H;
}

export function cardDotsPerRow(cardWidth: number): number {
  return Math.max(
    12,
    Math.floor(
      (cardWidth - CARD_PADDING_X * 2 - CARD_LABEL_W) / (CARD_BAND_DOT + CARD_BAND_DOT_GAP),
    ),
  );
}

export function cardBandHeight(dotCount: number, dotsPerRow: number): number {
  const shown = Math.min(dotCount, CARD_MAX_DOTS_PER_BAND);
  const rows = Math.max(1, Math.ceil(shown / dotsPerRow));
  return CARD_BAND_LABEL_H + rows * (CARD_BAND_DOT + CARD_BAND_DOT_GAP) - CARD_BAND_DOT_GAP;
}

/**
 * Compass Explorer card renderer. Built for density: a header with
 * kind + count + exposure/data signals, and a body that stacks
 * horizontal tier bands (ingress / compute / data / shared / network)
 * so the viewer's first glance tells them what the VPC is made of
 * without reading any text.
 *
 * Each band:
 *   - tier-coloured dot on the left
 *   - label + count
 *   - rail of dots (wrapped) so population is legible at both scales
 *
 * Dots are painted in the tier's colour. Card sizing comes from
 * `bubbleDimensions()` in buildExplorerScene, which mirrors the band
 * layout here.
 */
export const CompassContainerCard: React.FC<CompassContainerCardProps> = ({ viewModel, props }) => {
  const { width: w, height: h } = viewModel;
  const kind = String(props.kind ?? 'vpc');
  const label = String(props.label ?? '');
  const count = Number(props.resourceCount ?? 0);
  // Lens (P1.3) decoration stamped by ExplorerViewService. When absent
  // or dataMissing we fall back to the kind accent, keeping the card
  // visually stable for scans without the data required by the active
  // lens (e.g. cost lens on a no-cost scan).
  const lens = props.lens as
    | { color?: string; badge?: { label: string; tone?: string }; dataMissing?: boolean }
    | undefined;
  const lensAccent = lens?.color && !lens?.dataMissing ? lens.color : undefined;
  const accent = lensAccent ?? KIND_ACCENT[kind] ?? '#6366f1';
  const sectionAccent = typeof props.sectionAccent === 'string' ? props.sectionAccent : undefined;
  // Delta (P0.1 compare) — 'added' | 'removed' | 'changed'. Renderers
  // branch on this attr only — never on prefs.compareAgainstVersion.
  const delta = typeof props.delta === 'string' ? (props.delta as DeltaKind) : undefined;
  const expanded = Boolean(props.expanded);
  const dimmed = Boolean(props.dimmed);
  const provisional = Boolean(props.provisional);
  const interior = (props.interior as InteriorDotLite[] | undefined) ?? [];
  const boundaryChips = (props.boundaryChips as BoundaryChip[] | undefined) ?? [];
  const findings = (props.findings as ExplorerFinding[] | undefined) ?? [];
  const bubbleId = String(props.bubbleId ?? '');
  const highlightedBubbleId = useCompassViewPrefs((s) => s.highlightedBubbleId);
  const isHighlighted = !!bubbleId && highlightedBubbleId === bubbleId;
  const isGhostVpc = findings.some((finding) => finding.ruleId === 'ghost_vpc' || finding.ruleId === 'dead_vpc');
  // Rolled-up worst severity + total finding count from the scene
  // builder's severity-attribution pass. Painted as a top-left dot
  // so a VPC / workload bubble surfaces "this group has problems"
  // before the viewer drills in.
  const rolledFindingSeverity = typeof props.findingSeverity === 'string'
    ? (props.findingSeverity as 'critical' | 'high' | 'medium' | 'low' | 'info')
    : null;
  const rolledFindingCount = typeof props.findingCount === 'number' ? props.findingCount : 0;

  const bands = groupByTier(interior);
  const hasIngress = bands.some((b) => b.tier === 'ingress' && b.dots.length > 0);
  const hasData = bands.some((b) => b.tier === 'data' && b.dots.length > 0);

  const dotsPerRow = cardDotsPerRow(w);

  // Signals surfaced in the header — answer "what am I looking at?"
  // before the viewer reads the body. Exposure (public surface) is
  // the most urgent; data presence hints at what's stored here.
  const signals: Array<{ icon: string; color: string; title: string }> = [];
  if (hasIngress) signals.push({ icon: 'EXP', color: '#d63a3a', title: 'Publicly exposed' });
  if (hasData) signals.push({ icon: 'DATA', color: '#1ea672', title: 'Has persistent data' });

  // Top severity from findings — drives the card's outline accent so
  // problem cards jump off the canvas.
  const topSeverity = findings.reduce<string | null>((acc, f) => {
    if (!acc) return f.severity;
    const order = ['info', 'warn', 'critical'];
    return order.indexOf(f.severity) > order.indexOf(acc) ? f.severity : acc;
  }, null);
  const findingOutline = topSeverity ? SEVERITY_COLOR[topSeverity] : null;
  const headerAccent = sectionAccent ?? accent;

  return (
    <Group
      opacity={dimmed ? 0.45 : 1}
      onMouseEnter={(e) => {
        const stage = e.target.getStage();
        const container = stage?.container();
        if (container) container.style.cursor = 'pointer';
      }}
      onMouseLeave={(e) => {
        const stage = e.target.getStage();
        const container = stage?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      {/* Body. Stroke precedence: highlight > delta > findingOutline > accent.
          Delta only wins when the scene is in compare mode (attrs.delta
          stamped). Provisional nodes (P1.4 proposed) render with a dashed
          outline so architects can see phantom additions. */}
      <Rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill={delta ? DELTA_TINT[delta].fill : CARD_FILL}
        stroke={
          isHighlighted
            ? NAVY_900
            : delta
              ? DELTA_TINT[delta].stroke
              : (findingOutline ?? sectionAccent ?? CARD_STROKE)
        }
        strokeWidth={isHighlighted ? 3 : (delta ? 2 : findingOutline ? 2 : expanded ? 2 : 1.25)}
        dash={provisional ? [6, 4] : undefined}
        cornerRadius={CARD_RADIUS}
        shadowColor="rgba(14, 23, 38, 0.18)"
        shadowBlur={12}
        shadowOffsetY={5}
        shadowOpacity={0.18}
        shadowForStrokeEnabled={false}
      />
      {isGhostVpc && (
        <Group
          clipX={0}
          clipY={0}
          clipWidth={w}
          clipHeight={h}
          listening={false}
          opacity={0.55}
        >
          {Array.from({ length: Math.ceil((w + h) / 18) }).map((_, i) => {
            const x = i * 18 - h;
            return (
              <Line
                key={`ghost-stripe-${i}`}
                points={[x, h - 8, x + h, 0]}
                stroke="#f3d7a7"
                strokeWidth={7}
                opacity={0.45}
              />
            );
          })}
        </Group>
      )}
      <Rect
        x={0.75}
        y={0.75}
        width={w - 1.5}
        height={h - 1.5}
        stroke="rgba(255,255,255,0.72)"
        strokeWidth={1}
        cornerRadius={CARD_RADIUS - 1}
        listening={false}
      />
      {/* Highlight halo — a soft purple ring when a findings row is
          clicked, so the viewer's eye lands on the right card. */}
      {isHighlighted && (
        <Rect
          x={-6}
          y={-6}
          width={w + 12}
          height={h + 12}
          stroke={NAVY_900}
          strokeWidth={2}
          opacity={0.24}
          cornerRadius={CARD_RADIUS + 4}
          listening={false}
        />
      )}
      <Rect
        x={0}
        y={0}
        width={w}
        height={3}
        fill={headerAccent}
        cornerRadius={[CARD_RADIUS, CARD_RADIUS, 0, 0]}
      />
      <Circle x={16} y={22} radius={4} fill={headerAccent} listening={false} />
      {/* Severity dot — top-left of the card, coloured by the worst
          finding severity across descendant resources. Count drawn
          inside when > 1 so a heavily-impacted bubble (e.g. 4 policy
          violations under one workload) jumps out from one with a
          single low signal. Hidden when the bubble has no findings. */}
      {rolledFindingSeverity && (() => {
        const sevPalette: Record<string, { fill: string; stroke: string }> = {
          critical: { fill: '#ef4444', stroke: '#7f1d1d' },
          high: { fill: '#f97316', stroke: '#7c2d12' },
          medium: { fill: '#eab308', stroke: '#713f12' },
          low: { fill: '#0ea5e9', stroke: '#0c4a6e' },
          info: { fill: '#94a3b8', stroke: '#334155' },
        };
        const palette = sevPalette[rolledFindingSeverity] ?? sevPalette.info;
        const size = 16;
        const dotX = -4;
        const dotY = -4;
        return (
          <Group listening={false}>
            <Circle
              x={dotX + size / 2}
              y={dotY + size / 2}
              radius={size / 2}
              fill={palette.fill}
              stroke={palette.stroke}
              strokeWidth={1.5}
            />
            {rolledFindingCount > 1 && (
              <Text
                x={dotX}
                y={dotY}
                width={size}
                height={size}
                text={String(rolledFindingCount)}
                fontSize={9}
                fontStyle="bold"
                fontFamily={FONT_SANS}
                fill="#ffffff"
                align="center"
                verticalAlign="middle"
              />
            )}
          </Group>
        );
      })()}
      <Text
        x={28}
        y={16}
        text={`${KIND_LABEL[kind] ?? kind.toUpperCase()} · ${count}`}
        fontSize={11}
        fontStyle="bold"
        fontFamily={FONT_SANS}
        fill="#405170"
        listening={false}
      />
      {/* Lens badge — shows the active lens's numeric reading for this
          bubble (cost roll-up / waste / utilisation / age). Suppressed
          when the lens has no data ("no cost") so the cards stay
          quiet on scans without the necessary signal. Painted with
          the lens colour so the eye links the badge to the card
          accent at a glance. */}
      {(() => {
        const lensBadge = lens?.badge;
        const shouldShowLensBadge =
          lensBadge?.label
            && !lens?.dataMissing
            && lensBadge.label.toLowerCase() !== 'no cost'
            && lensBadge.label.toLowerCase() !== 'no data';
        if (!shouldShowLensBadge || !lensBadge) return null;
        const text = lensBadge.label;
        const badgeW = Math.max(46, Math.min(108, 12 + text.length * 6));
        const offsetX = signals.length * (38 + 6);
        const chipX = w - badgeW - 8 - offsetX;
        // The lens decoration carries both a colour (heatmap gradient
        // value) and a tone (critical / warn / neutral threshold). The
        // raw heatmap colour reads fine on a card body but turns into
        // invisible light-green-on-white at the badge size, which was
        // exactly the "very faint" complaint. Lock the badge to a
        // contrast-safe palette driven by `tone`, and keep the heatmap
        // colour as a 4px left edge so the per-card gradient is still
        // legible at a glance.
        const tone = lensBadge.tone ?? 'neutral';
        const tonePalette: Record<string, { fill: string; stroke: string; text: string }> = {
          critical: { fill: '#fee2e2', stroke: '#ef4444', text: '#991b1b' },
          warn: { fill: '#fef3c7', stroke: '#f59e0b', text: '#92400e' },
          neutral: { fill: '#eef2ff', stroke: '#6366f1', text: '#3730a3' },
        };
        const palette = tonePalette[tone] ?? tonePalette.neutral;
        const edgeColor = lensAccent ?? palette.stroke;
        return (
          <Group x={chipX} y={8}>
            {/* Badge pill — contrast-safe by tone */}
            <Rect
              x={0}
              y={0}
              width={badgeW}
              height={16}
              fill={palette.fill}
              stroke={palette.stroke}
              strokeWidth={1}
              cornerRadius={8}
              listening={false}
            />
            {/* 3px left edge in the heatmap-gradient colour so the
                exact lens hue still shows; bold tone-text reads on top. */}
            <Rect
              x={0}
              y={0}
              width={3}
              height={16}
              fill={edgeColor}
              cornerRadius={[8, 0, 0, 8]}
              listening={false}
            />
            <Text
              x={2}
              y={4}
              width={badgeW - 4}
              text={text}
              fontSize={9}
              fontStyle="bold"
              fontFamily={FONT_SANS}
              fill={palette.text}
              align="center"
              listening={false}
            />
          </Group>
        );
      })()}

      {/* Header signals (EXP / DATA chips) */}
      {signals.map((s, i) => {
        const chipW = 38;
        const chipX = w - (signals.length - i) * (chipW + 6) - 8;
        return (
          <Group key={s.icon} x={chipX} y={8}>
            <Rect
              x={0}
              y={0}
              width={chipW}
              height={16}
              fill={withAlpha(s.color, 0.1)}
              cornerRadius={8}
              listening={false}
            />
            <Text
              x={0}
              y={4}
              width={chipW}
              text={s.icon}
              fontSize={9}
              fontStyle="bold"
              fontFamily={FONT_SANS}
              fill={s.color}
              align="center"
              listening={false}
            />
          </Group>
        );
      })}
      {/* Name + subtitle */}
      <Text
        x={12}
        y={40}
        width={w - 24}
        text={label}
        fontSize={14}
        fontStyle="bold"
        fontFamily={FONT_SANS}
        fill={INK_900}
        listening={false}
        ellipsis
        wrap="none"
      />
      {/* Scope breadcrumb — answers "where in the estate is this?" so
          a card like "5 × ALB" never floats without spatial context.
          Stamped at slice time by `computeBubbleScope` walking the
          member parent chain. When members span multiple VPCs the
          renderer falls back to "Multiple VPCs (3)" instead of a
          single name. */}
      {(() => {
        const scope = props.scope as
          | {
              account?: { id: string; label: string };
              region?: { id: string; label: string };
              vpc?: { id: string; label: string };
              subnet?: { id: string; label: string };
              multi?: boolean;
              vpcSpan?: number;
              subnetSpan?: number;
            }
          | undefined;
        if (!scope) return null;
        // Don't repeat the kind name back to itself. A VPC bubble
        // already reads "Production VPC" in the title — showing
        // "Production VPC" again as a breadcrumb is noise.
        const parts: string[] = [];
        if (scope.region && kind !== 'region' && kind !== 'account') {
          parts.push(scope.region.label);
        }
        if (scope.vpc && kind !== 'vpc') {
          parts.push(scope.vpc.label);
        } else if (scope.vpcSpan && scope.vpcSpan > 1 && kind !== 'vpc') {
          parts.push(`${scope.vpcSpan} VPCs`);
        }
        if (scope.subnet && kind !== 'subnet') {
          parts.push(scope.subnet.label);
        } else if (scope.subnetSpan && scope.subnetSpan > 1 && kind !== 'subnet' && kind !== 'vpc') {
          parts.push(`${scope.subnetSpan} subnets`);
        }
        if (parts.length === 0) return null;
        return (
          <Text
            x={12}
            y={58}
            width={w - 24}
            text={parts.join(' · ')}
            fontSize={10}
            fontFamily={FONT_SANS}
            fill={INK_500}
            listening={false}
            ellipsis
            wrap="none"
          />
        );
      })()}
      {/* Finding badges under the name — short titles, coloured by
          severity. Cap at 3 to avoid the card body losing room for
          tier bands. */}
      {findings.length > 0 && (
        <Group x={12} y={58}>
          {findings.slice(0, 3).map((f, i) => {
            const badgeColor = SEVERITY_COLOR[f.severity] ?? '#64748b';
            const badgeW = 8 + f.title.length * 6;
            const x = i === 0 ? 0 : findings.slice(0, i).reduce(
              (acc, prev) => acc + 8 + prev.title.length * 6 + 6,
              0,
            );
            return (
              <Group key={f.id} x={x}>
                <Rect
                  x={0}
                  y={0}
                  width={badgeW}
                  height={16}
                  fill={withAlpha(badgeColor, 0.1)}
                  cornerRadius={8}
                  listening={false}
                />
                <Text
                  x={4}
                  y={3}
                  text={f.title}
                  fontSize={10}
                  fontStyle="bold"
                  fontFamily={FONT_SANS}
                  fill={badgeColor}
                  listening={false}
                />
              </Group>
            );
          })}
        </Group>
      )}
      {/* Tier bands */}
      <Group x={CARD_PADDING_X} y={cardHeaderHeight(findings.length > 0) + 4}>
        {(() => {
          let y = 0;
          return bands.map((band) => {
            const bandH = cardBandHeight(band.dots.length, dotsPerRow);
            const shown = Math.min(band.dots.length, CARD_MAX_DOTS_PER_BAND);
            const overflow = band.dots.length - shown;
            const labelText = overflow > 0
              ? `${band.label} · ${band.dots.length} (+${overflow})`
              : `${band.label} · ${band.dots.length}`;
            const bandY = y;
            y += bandH + CARD_BAND_GAP;
            return (
              <Group key={band.tier} x={0} y={bandY}>
                {/* Tier swatch + label — both vertically centred against
                    the band-label strip so the dot aligns to the
                    visual middle of the text instead of sitting above
                    the baseline. */}
                <Circle
                  x={4}
                  y={CARD_BAND_LABEL_H / 2}
                  radius={4}
                  fill={band.color}
                  opacity={0.9}
                  listening={false}
                />
                <Text
                  x={14}
                  y={0}
                  width={CARD_LABEL_W - 14}
                  height={CARD_BAND_LABEL_H}
                  verticalAlign="middle"
                  text={labelText}
                  fontSize={11}
                  fontStyle="bold"
                  fontFamily={FONT_SANS}
                  fill={INK_700}
                  listening={false}
                />
                {/* Dots rail.
                    Each card carries up to 80 dots per tier × ~5 tiers
                    and there are 11 VPC cards on a typical L2 overview —
                    that's ~4,400 Konva Circles per scene. Listening +
                    per-dot event handlers were forcing Konva to hit-test
                    every one of them on every mouse move during pan,
                    which is exactly the "super slow / laggy" feel.
                    Dots are now non-interactive (listening=false) —
                    drilling stays available via the card-level
                    "→ Open" affordance below the body. We also disable
                    `perfectDrawEnabled` so Konva skips the stroke-edge
                    smoothing pass per shape. The visible result is
                    indistinguishable at the dot's 4-6px size and orders
                    of magnitude faster to repaint. */}
                <Group x={CARD_LABEL_W} y={CARD_BAND_LABEL_H} listening={false}>
                  {band.dots.slice(0, shown).map((dot, i) => {
                    const col = i % dotsPerRow;
                    const row = Math.floor(i / dotsPerRow);
                    return (
                      <Circle
                        key={dot.id}
                        x={col * (CARD_BAND_DOT + CARD_BAND_DOT_GAP) + CARD_BAND_DOT / 2}
                        y={row * (CARD_BAND_DOT + CARD_BAND_DOT_GAP) + CARD_BAND_DOT / 2}
                        radius={CARD_BAND_DOT / 2}
                        fill={band.color}
                        opacity={0.72}
                        stroke={withAlpha(band.color, 0.42)}
                        strokeWidth={0.75}
                        listening={false}
                        perfectDrawEnabled={false}
                        shadowForStrokeEnabled={false}
                      />
                    );
                  })}
                </Group>
              </Group>
            );
          });
        })()}
      </Group>
      {bands.length === 0 && (
        <Group x={16} y={cardHeaderHeight(findings.length > 0) + 6} listening={false}>
          <Rect
            x={0}
            y={0}
            width={w - 32}
            height={30}
            fill="#f4f6fa"
            cornerRadius={6}
          />
          <Text
            x={0}
            y={8}
            width={w - 32}
            text="No tracked resources"
            fontSize={11}
            fontFamily={FONT_SANS}
            fill="#91a0b7"
            align="center"
          />
        </Group>
      )}
      <Line
        points={[12, h - CARD_FOOTER_H, w - 12, h - CARD_FOOTER_H]}
        stroke="#dfe5ee"
        strokeWidth={1}
        dash={[3, 2]}
        listening={false}
      />
      <Text
        x={12}
        y={h - 25}
        text={`${count} ${count === 1 ? 'RESOURCE' : 'RESOURCES'}`}
        fontSize={10}
        fontFamily={FONT_SANS}
        fill="#7a8aa5"
        letterSpacing={0.8}
        listening={false}
      />
      {/* Drill affordance. It behaves like linked-diagram navigation:
          selecting the card is separate from following it. */}
      <Group
        x={w - 35}
        y={h - 33}
        onClick={(e: KonvaEventObject<MouseEvent>) => {
          e.cancelBubble = true;
          dispatchDrillBubble(bubbleId);
        }}
        onTap={(e: KonvaEventObject<TouchEvent>) => {
          e.cancelBubble = true;
          dispatchDrillBubble(bubbleId);
        }}
        onMouseEnter={(e: KonvaEventObject<MouseEvent>) => {
          const stage = e.target.getStage();
          const container = stage?.container();
          if (container) container.style.cursor = 'pointer';
        }}
        onMouseLeave={(e: KonvaEventObject<MouseEvent>) => {
          const stage = e.target.getStage();
          const container = stage?.container();
          if (container) container.style.cursor = 'default';
        }}
      >
        <Text
          x={0}
          y={0}
          width={24}
          text="→"
          fontSize={25}
          fontStyle="bold"
          fontFamily={FONT_SANS}
          fill="#05070c"
          align="center"
        />
      </Group>
      {/* Boundary chips — on the right edge of expanded bubbles whose
          peers are still collapsed. */}
      {expanded && boundaryChips.length > 0 && (
        <Group x={w} y={48}>
          {boundaryChips.slice(0, 6).map((chip, i) => {
            const chipLabel = `${chip.direction === 'out' ? '→' : '←'} ${chip.targetLabel}${chip.count > 1 ? ` ×${chip.count}` : ''}`;
            const chipW = Math.min(200, 24 + chipLabel.length * 6);
            const y = i * 24;
            return (
              <Group key={`${chip.targetBubbleId}-${chip.direction}`} x={-6} y={y}>
                <Rect
                  x={0}
                  y={0}
                  width={chipW}
                  height={20}
                  fill={NAVY_100}
                  stroke={withAlpha(accent, 0.4)}
                  strokeWidth={1}
                  cornerRadius={10}
                  listening={false}
                />
                <Text
                  x={8}
                  y={4}
                  text={chipLabel}
                  fontSize={11}
                  fontFamily={FONT_SANS}
                  fill={INK_500}
                  listening={false}
                />
              </Group>
            );
          })}
        </Group>
      )}
    </Group>
  );
};
